import React, { useState, useEffect } from "react";
import { Construction, Clock, Mail } from "lucide-react";

const MaintenancePage: React.FC = () => {
  const [currentLang, setCurrentLang] = useState<"ro" | "it">("ro");

  // Alternate language every 10 seconds for international visitors
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLang((prev) => (prev === "ro" ? "it" : "ro"));
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const translations = {
    ro: {
      title: "În Curând",
      subtitle: "Ne pregătim să vă oferim o experiență de neuitat!",
      description:
        "Site-ul nostru este în curs de finalizare. Lucrăm cu drag pentru a vă aduce cea mai bună experiență a festivalului Ca Pe Vremuri.",
      comingSoon: "Festival de Muzică și Tradiție Românească",
      stayTuned: "Rămâneți aproape!",
      contact: "Pentru orice întrebare, contactați-ne:",
    },
    it: {
      title: "Prossimamente",
      subtitle:
        "Ci stiamo preparando per offrirti un'esperienza indimenticabile!",
      description:
        "Il nostro sito è in fase di completamento. Stiamo lavorando con passione per portarti la migliore esperienza del festival Ca Pe Vremuri.",
      comingSoon: "Festival di Musica e Tradizione Romena",
      stayTuned: "Restate sintonizzati!",
      contact: "Per qualsiasi domanda, contattateci:",
    },
  };

  const t = translations[currentLang];

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-romanian-blue via-primary-600 to-romanian-blue">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Romanian Tricolor Stripes */}
        <div className="absolute -left-20 top-0 w-40 h-full bg-gradient-to-b from-romanian-blue/30 via-romanian-yellow/30 to-romanian-red/30 transform -skew-x-12 blur-xl" />
        <div className="absolute -right-20 top-0 w-40 h-full bg-gradient-to-b from-romanian-blue/30 via-romanian-yellow/30 to-romanian-red/30 transform skew-x-12 blur-xl" />

        {/* Floating Circles */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-romanian-yellow/20 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-20 right-10 w-96 h-96 bg-romanian-red/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-1/3 w-48 h-48 bg-white/10 rounded-full blur-2xl animate-pulse"
          style={{ animationDelay: "2s" }}
        />

        {/* Traditional Pattern Overlay */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="h-full w-full"
            style={{
              backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(255,255,255,0.1) 10px,
              rgba(255,255,255,0.1) 20px
            )`,
            }}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-12">
        {/* Logo Container */}
        <div className="mb-8 animate-bounce-slow">
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-romanian-yellow/30 rounded-full blur-2xl transform scale-150" />

            {/* Logo Image */}
            <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-romanian-yellow shadow-2xl">
              <img
                src="/logo.png"
                alt="Ca Pe Vremuri"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Romanian Ornament */}
        <div className="flex items-center justify-center space-x-4 mb-6">
          <div className="w-12 md:w-20 h-1 bg-gradient-to-r from-transparent to-romanian-yellow rounded-full" />
          <div className="w-3 h-3 bg-romanian-yellow rotate-45 transform hover:rotate-[135deg] transition-transform duration-300" />
          <div className="w-12 md:w-20 h-1 bg-gradient-to-l from-transparent to-romanian-yellow rounded-full" />
        </div>

        {/* Main Title */}
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-4 text-center tracking-tight">
          Ca Pe Vremuri
        </h1>

        {/* Coming Soon Badge */}
        <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full mb-6 border border-white/20">
          <Construction className="w-5 h-5 text-romanian-yellow animate-pulse" />
          <span className="text-romanian-yellow font-display text-lg md:text-xl font-semibold">
            {t.title}
          </span>
          <Clock className="w-5 h-5 text-romanian-yellow animate-pulse" />
        </div>

        {/* Subtitle */}
        <p className="text-white/90 text-lg md:text-2xl text-center max-w-2xl mb-4 font-light">
          {t.subtitle}
        </p>

        {/* Festival Type */}
        <p className="text-romanian-yellow/80 text-sm md:text-base font-display mb-8">
          {t.comingSoon}
        </p>

        {/* Description Card */}
        <div className="max-w-xl mx-auto bg-white/10 backdrop-blur-lg rounded-2xl p-6 md:p-8 border border-white/20 shadow-2xl mb-10">
          <p className="text-white/80 text-center leading-relaxed">
            {t.description}
          </p>
        </div>

        {/* Stay Tuned */}
        <p className="text-white font-display text-xl mb-6 animate-pulse">
          {t.stayTuned}
        </p>

        {/* Contact Section */}
        <div className="text-center mb-8">
          <p className="text-white/70 text-sm mb-4">{t.contact}</p>
          <a
            href="mailto:info@capevremuri.it"
            className="inline-flex items-center gap-2 text-romanian-yellow hover:text-white transition-colors duration-300 group"
          >
            <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
            <span className="font-medium">info@capevremuri.it</span>
          </a>
        </div>

        {/* Language Toggle */}
        <div className="absolute top-6 right-6 flex items-center gap-2">
          <button
            onClick={() => setCurrentLang("ro")}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
              currentLang === "ro"
                ? "bg-romanian-yellow text-romanian-blue"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            🇷🇴 RO
          </button>
          <button
            onClick={() => setCurrentLang("it")}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
              currentLang === "it"
                ? "bg-romanian-yellow text-romanian-blue"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            🇮🇹 IT
          </button>
        </div>

        {/* Bottom Romanian Ornament */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-romanian-blue rounded-full" />
            <div className="w-2 h-2 bg-romanian-yellow rounded-full" />
            <div className="w-2 h-2 bg-romanian-red rounded-full" />
          </div>
        </div>
      </div>

      {/* Custom Animation Styles */}
      <style>{`
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default MaintenancePage;
